import { Localized, useLocalization } from "@fluent/react";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItemText, Paper, Skeleton, Stack, TextField, Typography, useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/material/styles";
import { AppTextFieldHandler } from "../../../app/appTypes";
import { addMailingGroup, MailingGroup, MailingGroupUser, updateMailingGroup } from "./mailingGroupApi";
import { If } from "../../../app/If";
import { useCallback, useState, KeyboardEvent } from "react";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LoadUsers } from "../../../app/AppDataLoader";
import { User, selectAllUsers } from "../../users/usersSlice";
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { LpField } from "../../../app/components/LpField";


type AddEditMailingGroupDialogMode = 'create' | 'edit'

const userDisplayName = (user: User) =>
  `${user.firstname ?? ""} ${user.surname ?? ""}`.trim() || user.userName;

const includeSelectedUsers = (emails: string[], users: User[]) => {
  const knownEmails = new Set(emails.map(email => email.trim().toLowerCase()));
  const addedEmails = users.map(user => user.email.trim()).filter(email => {
    const key = email.toLowerCase();
    if (knownEmails.has(key)) return false;
    knownEmails.add(key);
    return true;
  });
  return [...emails, ...addedEmails];
};

export interface AddEditMailingGroupDialogProps {
  mailingGroup: MailingGroup | undefined
  mode: AddEditMailingGroupDialogMode;
  onSuccessfullySaved: (updatedGroup: MailingGroup) => void;
  onCancel: () => void
}

export const AddEditMailingGroupDialog: React.FC<AddEditMailingGroupDialogProps> = ({ mailingGroup, mode, onSuccessfullySaved, onCancel }) => {
  const { l10n } = useLocalization()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))
  const allUsers = useAppSelector(selectAllUsers)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState(mailingGroup?.name)
  const [groupEmail, setGroupEmail] = useState(mailingGroup?.email)
  const [serverError, setServerError] = useState("")
  const [emails, setEmails] = useState<string[]>(
    mailingGroup?.users?.map((user) => user.email).filter((email) => !!email) || []
  );
  const [emailInput, setEmailInput] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [addingUsers, setAddingUsers] = useState(false);
  const [selectedMemberEmails, setSelectedMemberEmails] = useState<Set<string>>(() => new Set());
  const [userSearch, setUserSearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const selectedUserIds = new Set(selectedUsers.map(user => user.id));
  const usersByEmail = new Map(allUsers.map(user => [user.email.trim().toLowerCase(), user]));
  const memberKeys = new Set(emails.map(email => email.trim().toLowerCase()));
  const availableUsers = allUsers.filter(user =>
    user.email?.trim() && !memberKeys.has(user.email.trim().toLowerCase()))
    .sort((a, b) => a.email.localeCompare(b.email));
  const visibleUsers = availableUsers.filter(user =>
    `${user.firstname} ${user.surname} ${user.userName} ${user.email}`
      .toLocaleLowerCase().includes(userSearch.trim().toLocaleLowerCase()));
  const visibleMembers = [...emails].sort((a, b) => a.localeCompare(b)).filter(email => {
    const user = usersByEmail.get(email.trim().toLowerCase());
    return `${user?.firstname ?? ""} ${user?.surname ?? ""} ${user?.userName ?? ""} ${email}`
      .toLocaleLowerCase().includes(memberSearch.trim().toLocaleLowerCase());
  });

  const handleMailingGroupNameChange: AppTextFieldHandler = useCallback(e => {
    setName(e.target.value)
  }, [])

  const handleEmailChange: AppTextFieldHandler = useCallback(e => {
    setGroupEmail(e.target.value)
  }, [])


  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (email !== '') {
      if (!validateEmail(email)) {
        setEmailError(l10n.getString("email-incorrect", null, "_Nieprawidłowy email"));
      } else if (emails.some(existing => existing.trim().toLowerCase() === email.toLowerCase())) {
        setEmailError(l10n.getString("email-already-on-list", null, "_Email jest już na liście"));
      } else {
        setEmails([...emails, email]);
        setEmailInput('');
        setEmailError('');
      }
    }
  };

  const handleAddUsers = () => {
    setEmails(current => includeSelectedUsers(current, selectedUsers));
    setSelectedUsers([]);
  };

  const handleRemoveMembers = () => {
    setEmails(current => current.filter(email => !selectedMemberEmails.has(email.toLowerCase())));
    setSelectedMemberEmails(new Set());
  };

  const removeMember = (email: string) => {
    setEmails(current => current.filter(item => item.toLowerCase() !== email.toLowerCase()));
    setSelectedMemberEmails(current => {
      const next = new Set(current);
      next.delete(email.toLowerCase());
      return next;
    });
  };

  const toggleUser = (user: User) => {
    setSelectedUsers(current => current.some(item => item.id === user.id)
      ? current.filter(item => item.id !== user.id)
      : [...current, user]);
  };

  const toggleMember = (email: string) => {
    setSelectedMemberEmails(current => {
      const next = new Set(current);
      const key = email.toLowerCase();
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleVisibleUsers = () => {
    setSelectedUsers(current => {
      const selectedIds = new Set(current.map(user => user.id));
      const allSelected = visibleUsers.every(user => selectedIds.has(user.id));
      const visibleIds = new Set(visibleUsers.map(user => user.id));
      return allSelected
        ? current.filter(user => !visibleIds.has(user.id))
        : [...current, ...visibleUsers.filter(user => !selectedIds.has(user.id))];
    });
  };

  const toggleVisibleMembers = () => {
    setSelectedMemberEmails(current => {
      const next = new Set(current);
      const allSelected = visibleMembers.every(email => next.has(email.toLowerCase()));
      visibleMembers.forEach(email => {
        if (allSelected) next.delete(email.toLowerCase());
        else next.add(email.toLowerCase());
      });
      return next;
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAddEmail();
    }
  };

  const handleSaveClick = async () => {
    setSaving(true)

    const generatedUsers: MailingGroupUser[] = includeSelectedUsers(
      emails.filter(email => !selectedMemberEmails.has(email.toLowerCase())), selectedUsers
    ).map(email => ({
      id: "0",
      firstName: "",
      surname: "",
      email: email,
    }))

    try {
      switch (mode) {
        case "create":
          if (groupEmail && name) {

            const newGroup: MailingGroup = {
              name: name,
              email: groupEmail,
              id: "0",
              users: generatedUsers
            };

            const result = await dispatch(addMailingGroup(newGroup))
            if (addMailingGroup.fulfilled.match(result)) {
              const newMailingGroup = result.payload as MailingGroup;
              dispatch(showSuccess("saved"))
              onSuccessfullySaved(newMailingGroup)
            } else {
              let errorMsg = "error"
              if (result.payload && result.payload.kind === 'http') {
                const problem = result.payload.problem
                if (problem) {
                  errorMsg = problem.title

                }
              }
              setServerError(errorMsg)
            }

          }

          break
        case "edit":

          if (groupEmail && name) {

            const updatedGroup: MailingGroup = {
              name: name,
              email: groupEmail,
              id: mailingGroup?.id || "0",
              users: generatedUsers
            };

            const result = await dispatch(updateMailingGroup(updatedGroup))
            if (updateMailingGroup.fulfilled.match(result)) {
              const newMailingGroup = result.payload as MailingGroup;
              dispatch(showSuccess("saved"))
              onSuccessfullySaved(newMailingGroup)
            } else {
              let errorMsg = "error"
              if (result.payload && result.payload.kind === 'http') {
                const problem = result.payload.problem
                if (problem) {
                  errorMsg = problem.title

                }
              }
              setServerError(errorMsg)
            }

          }


          break
      }

    }
    catch (error) {
      dispatch(showError("error"));
    }
    finally {
      setSaving(false)
    }
  }

  return <>
    <Dialog open={true} fullWidth fullScreen={fullScreen} maxWidth={addingUsers ? "lg" : "md"}
      PaperProps={{ sx: { height: fullScreen ? "100%" : addingUsers
        ? "min(780px, calc(100dvh - 48px))" : "min(620px, calc(100dvh - 48px))" } }}>
      <DialogTitle sx={{ bgcolor: "grey.200", color: "text.primary", fontWeight: "bold" }}>
        <If condition={mode === "create"} otherwise={<Localized id="company-settings-mailing-group-edit">_Edytowanie grupy mailingowej</Localized>}>
          <Localized id="company-settings-mailing-group-create">_Tworzenie grupy mailingowej</Localized>
        </If>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ py: 2 }}>
          <TextField
            fullWidth
            value={name}
            inputProps={{ maxLength: 20 }}
            onChange={handleMailingGroupNameChange}
            label={<Localized id="company-settings-mailing-name">_name</Localized>}>
          </TextField>
          <TextField
            fullWidth
            value={groupEmail}
            inputProps={{ maxLength: 20 }}
            onChange={handleEmailChange}
            label={<Localized id="company-settings-mailing-email">_email</Localized>}>
          </TextField>

        </Stack>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: addingUsers ? "1fr 1fr" : "1fr" },
          gridTemplateRows: { xs: addingUsers ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)", sm: "minmax(0, 1fr)" },
          gap: 2, flex: 1, minHeight: 0
        }}>
          <Paper elevation={2} sx={{
            display: "flex", flexDirection: "column", minHeight: 0,
            gridColumn: { sm: addingUsers ? 2 : 1 }, gridRow: { sm: 1 }
          }}>
            <Box sx={{
              p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
              bgcolor: "grey.100", borderBottom: 1, borderColor: "divider"
            }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {l10n.getString("company-settings-mailing-members", { count: emails.length }, `_Członkowie grupy (${emails.length})`)}
              </Typography>
              <Button size="small" disabled={saving} variant={addingUsers ? "outlined" : "contained"}
                startIcon={addingUsers ? <ArrowBackIcon /> : <PersonAddIcon />}
                onClick={() => setAddingUsers(value => !value)}>
                {addingUsers
                  ? l10n.getString("company-settings-mailing-back-to-members", null, "_Wróć do listy członków")
                  : l10n.getString("company-settings-mailing-add-users", null, "_Dodaj użytkowników")}
              </Button>
            </Box>
            <Box sx={{ px: 2, py: 1 }}>
              <TextField fullWidth size="small" value={memberSearch}
                onChange={event => setMemberSearch(event.target.value)}
                label={l10n.getString("company-settings-mailing-search-members", null, "_Szukaj członków grupy")} />
              <Stack direction="row" gap={1} alignItems="center">
                <Button size="small" disabled={saving || visibleMembers.length === 0}
                  onClick={toggleVisibleMembers}>
                  <Localized id="company-settings-mailing-toggle-visible">_Zaznacz / odznacz widocznych</Localized>
                </Button>
                <Button size="small" color="error" disabled={saving || selectedMemberEmails.size === 0}
                  onClick={handleRemoveMembers}>
                  <Localized id="company-settings-mailing-remove-selected">_Usuń wybranych</Localized>
                </Button>
              </Stack>
            </Box>
            <List dense sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 0 }}>
              {visibleMembers.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  <Localized id="company-settings-mailing-no-members">_Brak pasujących członków grupy</Localized>
                </Typography>
              )}
              {visibleMembers.map((email, index) => {
                const user = usersByEmail.get(email.trim().toLowerCase());
                return (
                  <Box key={email} sx={{
                    px: 2, py: 1, display: "flex", alignItems: "center", gap: 1,
                    borderBottom: 1, borderColor: "divider"
                  }}>
                    <Checkbox size="small" disabled={saving}
                      checked={selectedMemberEmails.has(email.toLowerCase())}
                      onChange={() => toggleMember(email)} />
                    <LpField value={index + 1} sx={{ flexShrink: 0 }} />
                    <ListItemText sx={{ minWidth: 0 }} primary={user ? userDisplayName(user) : email}
                      secondary={user ? email : undefined} />
                    <IconButton size="small" disabled={saving} onClick={() => removeMember(email)}
                      aria-label={l10n.getString("company-settings-mailing-remove-one", null, "_Usuń członka")}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </List>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", borderTop: 1, borderColor: "divider" }}>
              <TextField fullWidth size="small" value={emailInput}
                onChange={event => setEmailInput(event.target.value)}
                onKeyDown={handleKeyDown}
                label={<Localized id="add-email">_Dodaj email</Localized>}
                error={!!emailError} helperText={emailError} />
              <IconButton onClick={handleAddEmail} disabled={saving} color="primary"
                aria-label={l10n.getString("add-email", null, "_Dodaj email")}>
                <AddIcon />
              </IconButton>
            </Box>
          </Paper>
          {addingUsers && (
            <LoadUsers component={<Skeleton height={120} />}>
              <Paper variant="outlined" sx={{
                display: "flex", flexDirection: "column", minHeight: 0,
                gridColumn: { sm: 1 }, gridRow: { sm: 1 }
              }}>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    <Localized id="company-settings-mailing-available-users">_Dostępni użytkownicy</Localized>
                  </Typography>
                  <TextField fullWidth size="small" value={userSearch}
                    onChange={event => setUserSearch(event.target.value)}
                    label={l10n.getString("company-settings-mailing-search-users", null, "_Szukaj po nazwie lub e-mailu")}
                    sx={{ mt: 1 }} />
                  <Stack direction="row" gap={1} alignItems="center">
                    <Button size="small" disabled={saving || visibleUsers.length === 0}
                      onClick={toggleVisibleUsers}>
                      <Localized id="company-settings-mailing-toggle-visible">_Zaznacz / odznacz widocznych</Localized>
                    </Button>
                    <Button size="small" disabled={saving || selectedUsers.length === 0}
                      onClick={handleAddUsers}>
                      <Localized id="company-settings-mailing-add-selected">_Dodaj wybranych</Localized>
                    </Button>
                  </Stack>
                </Box>
                <List dense sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 0 }}>
                  {visibleUsers.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      <Localized id="company-settings-mailing-no-users">_Brak pasujących użytkowników</Localized>
                    </Typography>
                  )}
                  {visibleUsers.map((user, index) => (
                    <Box key={user.id} sx={{
                      px: 2, py: 1, display: "flex", alignItems: "center", gap: 1,
                      borderBottom: 1, borderColor: "divider"
                    }}>
                      <Checkbox size="small" disabled={saving} checked={selectedUserIds.has(user.id)}
                        onChange={() => toggleUser(user)} />
                      <LpField value={index + 1} sx={{ flexShrink: 0 }} />
                      <ListItemText sx={{ minWidth: 0 }} primary={`${userDisplayName(user)} (${user.userName})`}
                        secondary={[user.email, user.position].filter(Boolean).join(" | ")} />
                      <IconButton size="small" color="primary" disabled={saving}
                        aria-label={l10n.getString("company-settings-mailing-add-one", null, "_Dodaj członka")}
                        onClick={() => {
                          setEmails(current => includeSelectedUsers(current, [user]));
                          setSelectedUsers(current => current.filter(item => item.id !== user.id));
                        }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </List>
              </Paper>
            </LoadUsers>
          )}
        </Box>
        {
          serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
            <Localized id={serverError}>
              <span>Server error</span>
            </Localized>
          </Alert>
        }
      </DialogContent>
      <DialogActions>
        <SaveButton loading={saving} onClick={handleSaveClick} />
        <CancelButton loading={saving} onClick={onCancel} />
      </DialogActions>
    </Dialog>
  </>
}
